import { useRef, useState } from "react";
import { IconCamera, IconExternalLink, IconTrash, IconCheck } from "@tabler/icons-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { LoadingImage } from "@/components/loading-image";
import { ResponsiveModal } from "@/components/responsive-modal";

interface PhotoUploadDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Currently saved photo URL (thumbnail or full). */
  photoUrl?: string | null;
  /** Whether an upload is in progress. */
  isUploading: boolean;
  /** Upload progress percentage (0-100). */
  uploadProgress?: number;
  /** Whether a delete is in progress. */
  isDeleting: boolean;
  /** True briefly after a successful upload, before auto-close. */
  justUploaded: boolean;
  /** Has an existing photo that can be retaken/deleted. */
  hasPhoto: boolean;
  onFileSelected: (file: File) => void;
  onDelete: () => void;
}

export function PhotoUploadDrawer({
  open,
  onOpenChange,
  title,
  photoUrl,
  isUploading,
  uploadProgress = 0,
  isDeleting,
  justUploaded,
  hasPhoto,
  onFileSelected,
  onDelete,
}: PhotoUploadDrawerProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [preview, _setPreview] = useState<string | null>(null);

  // Preview is only meaningful while open — always read as null when closed
  const effectivePreview = open ? preview : null;
  const setPreview = (v: string | null) => _setPreview(v);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    onFileSelected(file);
    e.target.value = "";
  };

  const displayUrl = effectivePreview ?? photoUrl;
  const isBusy = isUploading || isDeleting;

  const footer = !isBusy && !justUploaded && hasPhoto ? (
    <div className="flex gap-2">
      <Button
        variant="outline"
        size="sm"
        className="flex-1"
        onClick={() => fileRef.current?.click()}
      >
        <IconCamera className="mr-1.5 h-4 w-4" />
        Replace
      </Button>
      {photoUrl && (
        <Button
          variant="outline"
          size="sm"
          asChild
        >
          <a href={photoUrl} target="_blank" rel="noopener noreferrer">
            <IconExternalLink className="h-4 w-4" />
          </a>
        </Button>
      )}
      <Button
        variant="outline"
        size="sm"
        className="text-destructive hover:text-destructive"
        onClick={onDelete}
      >
        <IconTrash className="h-4 w-4" />
      </Button>
    </div>
  ) : undefined;

  return (
    <ResponsiveModal
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={footer}
    >
      {displayUrl ? (
        <div className="flex flex-col gap-3">
          <LoadingImage
            src={displayUrl}
            alt={title}
            className="w-full max-h-[70vh] sm:max-h-[50vh] object-contain rounded-md"
          />
          {isUploading && (
            <div className="flex flex-col gap-2 px-1">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium">Uploading...</span>
                <span className="text-sm text-muted-foreground">{uploadProgress}%</span>
              </div>
              <Progress value={uploadProgress} className="h-2" />
              <span className="text-xs text-muted-foreground">You can close this and continue</span>
            </div>
          )}
          {justUploaded && (
            <div className="flex items-center gap-2.5 px-1">
              <div className="h-5 w-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0">
                <IconCheck className="h-3 w-3 text-white" />
              </div>
              <span className="text-sm font-medium text-emerald-600 dark:text-emerald-400">Saved</span>
            </div>
          )}
        </div>
      ) : !isBusy ? (
        <div className="flex flex-col items-center gap-3 py-8">
          <p className="text-sm text-muted-foreground">No photo attached</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => fileRef.current?.click()}
          >
            <IconCamera className="mr-1.5 h-4 w-4" />
            Add Photo
          </Button>
        </div>
      ) : null}

      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleFileChange}
      />
    </ResponsiveModal>
  );
}
